import './types/global.d'
import 'iframe-resizer/js/iframeResizer.contentWindow'
import './index.css'

import { StyleInjectionProvider } from '@epilot/concorde-elements'
import { lazy, StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import { I18nextProvider } from 'react-i18next'

import { SpinnerPage } from './components/SpinnerPage'
import { captureSsoCallback } from './blocks-renderers/services/sso-service/callback-capture'
import { setGlobalPackages } from './globalPackages'
import { instance18n } from './locales/i18n'
import { initDatadog } from './services/datadog'
import { createShadowFormElement } from './shadow-form/shadow-form'
import {
  TIME_TO_FIRST_RENDER_METRIC,
  TIME_TO_READINESS,
  VERSION
} from './utils/config'
import { isThirdPartyCookiesEnabled } from './utils/cookies'
import { debug } from './utils/debug'
import { setOwnsDocument } from './utils/embed'
import { env } from './utils/env'
import { startTimer } from './utils/timers'
import { TRACE_KEYS, generateTraceId } from './utils/trace'

// Served from our own index.html, so this document is ours — iframe or not.
setOwnsDocument(true)

/** Must run before the URL is parsed for app params: OIDC response params would otherwise be
 * swallowed as journey context values. Also puts back the pre-redirect URL, journeyId included. */
captureSsoCallback()

// Expose some internal packages as global variables for other apps
setGlobalPackages()

debug('Journey App started', {
  STAGE: env('REACT_APP_STAGE'),
  VERSION
})

/**
 * WARNING: This should be always called and should happen before anything else,
 * thus making sure we can trace all sessions, including those that fail early.
 *
 * NOTE: This is supposed to be disabled if third-party tracking is not enabled,
 * but also for the test/local environments.
 */
initDatadog({
  disabled:
    !isThirdPartyCookiesEnabled() ||
    ['test', 'local'].includes(env('REACT_APP_STAGE') ?? 'unknown'),
  mode: 'index'
})

/**
 * Generates an unique trace id for this App's session.
 *
 * NOTE: This should be called before anything else, otherwise we might loose the ability
 * to trace the session to underlying errors/resources.
 */
generateTraceId(TRACE_KEYS.JOURNEY_SESSION_ID)

/**
 * Marks the beginning of times :)
 *
 * We wan't this metric to include the time spent in all operations that preceed
 * the rendering of the first step, thus we should start it as soon as possible.
 */
startTimer(
  TIME_TO_FIRST_RENDER_METRIC,
  'Time spent by the Journey App up until the first render, \
    which is when the user is able to see the actual content of the Journey.'
)

startTimer(
  TIME_TO_READINESS,
  'Time spent by the Journey App up until the App became ready to render the Journey.'
)

const AppShell = lazy(() => import('./AppShell'))

// TODO: reconsider how soon the AnalyticsProvider can be initialized

ReactDOM.render(
  <StyleInjectionProvider>
    <StrictMode>
      <I18nextProvider i18n={instance18n}>
        <Suspense fallback={<SpinnerPage />}>
          <AppShell />
        </Suspense>
      </I18nextProvider>
    </StrictMode>
  </StyleInjectionProvider>,
  document.getElementById('root')
)

createShadowFormElement()
